import React, { useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import { DropdownAction } from "../../../../../components/Dropdown/types";
import { DeletePopUp } from "../../../../../components/PopUp";
import { useResponsive } from "../../../../../hooks/useResponsive";
import { PAGE_SIZE } from "../../../../../utils/constants";
import { SECTION_CARD_BREAKPOINT } from "../../constants";
import { DateFormat, formatCurrency, formatDate } from "../../../../../utils";
import { InvestmentMarketUpdateItem } from "../../../../../services/investments/types";
import { useInvestmentMarketUpdates } from "../../hooks";
import { MarketUpdateRow } from "./MarketUpdateRow";
import { MarketUpdateCard } from "./MarketUpdateCard";
import { MarketUpdatesProps } from "./types";
import {
  MarketUpdatesCard,
  HeaderRow,
  HeaderToggle,
  Chevron,
  TitleBlock,
  Title,
  Subtitle,
  Badge,
  Body,
  Table,
  TableHead,
  HeaderCell,
  CardList,
  Footer,
  FooterText,
  LoadMoreLabel,
  Message,
  SkeletonRow,
  SkeletonLine,
} from "./styles";
import Button, { ButtonTypes } from "../../../../../components/Button";
import { SectionRetryButton } from "../../../../../components/SectionRetryButton";
import { KeyboardArrowDownRounded } from "@mui/icons-material";

const SKELETON_ROWS = [0, 1, 2, 3, 4];

const MarketUpdatesComponent: React.FC<MarketUpdatesProps> = ({
  id,
  onChanged,
  onEdit,
  onDuplicate,
}) => {
  const isMobile = useResponsive(SECTION_CARD_BREAKPOINT);
  const [isExpanded, setIsExpanded] = useState(false);
  const [pendingDelete, setPendingDelete] =
    useState<InvestmentMarketUpdateItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    items,
    totalRows,
    hasNext,
    isLoading,
    isLoadingMore,
    error,
    loadMore,
    removeMarketUpdate,
    reload,
  } = useInvestmentMarketUpdates({ id, onChanged });

  const shownCount = items.length;
  const remaining = Math.max(totalRows - shownCount, 0);
  const loadCount = Math.min(PAGE_SIZE, remaining);
  const hasItems = shownCount > 0;

  const badgeText = isMobile
    ? `${totalRows} updates`
    : `${shownCount} of ${totalRows} updates`;
  const footerText = isMobile
    ? `${shownCount} of ${totalRows} · newest first`
    : `Showing ${shownCount} of ${totalRows} · newest first`;

  const toggleExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  const buildRowActions = (
    item: InvestmentMarketUpdateItem,
  ): DropdownAction[] => [
    {
      id: "edit",
      label: "Edit update",
      icon: <EditIcon fontSize="small" />,
      onClick: () => onEdit?.(item),
      show: Boolean(onEdit),
    },
    {
      id: "duplicate",
      label: "Duplicate update",
      icon: <ContentCopyIcon fontSize="small" />,
      onClick: () => onDuplicate?.(item),
      show: Boolean(onDuplicate),
    },
    {
      id: "delete",
      label: "Delete update",
      icon: <DeleteIcon fontSize="small" />,
      onClick: () => setPendingDelete(item),
      variant: "destructive",
      show: true,
    },
  ];

  const closeDeleteModal = () => {
    if (!isDeleting) {
      setPendingDelete(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) {
      return;
    }
    setIsDeleting(true);
    await removeMarketUpdate(pendingDelete);
    setIsDeleting(false);
    setPendingDelete(null);
  };

  let body: React.ReactNode;
  if (isLoading && !hasItems) {
    body = (
      <Table>
        {SKELETON_ROWS.map((key) => (
          <SkeletonRow key={key}>
            <SkeletonLine $width="30%" />
            <SkeletonLine $width="20%" />
          </SkeletonRow>
        ))}
      </Table>
    );
  } else if (error && !hasItems) {
    body = (
      <>
        <Message>{error}</Message>
        <SectionRetryButton onClick={() => reload()} />
      </>
    );
  } else if (!hasItems) {
    body = <Message>No market updates logged yet.</Message>;
  } else if (isMobile) {
    body = (
      <CardList>
        {items.map((item) => (
          <MarketUpdateCard
            key={item.id}
            item={item}
            actions={buildRowActions(item)}
          />
        ))}
      </CardList>
    );
  } else {
    body = (
      <Table>
        <TableHead>
          <HeaderCell>Date</HeaderCell>
          <HeaderCell>New Value</HeaderCell>
          <HeaderCell>Change</HeaderCell>
          <HeaderCell $align="center">%</HeaderCell>
          <HeaderCell aria-hidden="true" />
        </TableHead>
        {items.map((item) => (
          <MarketUpdateRow
            key={item.id}
            item={item}
            actions={buildRowActions(item)}
          />
        ))}
      </Table>
    );
  }

  const showFooter = isExpanded && hasItems;
  const deleteText = pendingDelete ? (
    <>
      Do you really want to delete the market update of{" "}
      <strong>{formatCurrency(pendingDelete.value)}</strong> from{" "}
      <strong>{formatDate(pendingDelete.date, DateFormat.DMY_SPACE)}</strong>?
    </>
  ) : (
    ""
  );

  return (
    <MarketUpdatesCard aria-label="Investment market updates">
      <HeaderRow>
        <HeaderToggle
          type="button"
          onClick={toggleExpanded}
          aria-expanded={isExpanded}
          aria-label={
            isExpanded ? "Collapse market updates" : "Expand market updates"
          }
        >
          <Chevron $expanded={isExpanded}>
            <KeyboardArrowDownIcon fontSize="small" />
          </Chevron>
          <TitleBlock>
            <Title>Market updates</Title>
            <Subtitle $expanded={isExpanded}>
              Recorded portfolio values
            </Subtitle>
          </TitleBlock>
        </HeaderToggle>
        <Badge>{badgeText}</Badge>
      </HeaderRow>

      {isExpanded && <Body>{body}</Body>}

      {showFooter && (
        <Footer>
          <FooterText>{footerText}</FooterText>
          {hasNext && (
            <Button
              type={ButtonTypes.primary}
              onClick={loadMore}
              disabled={isLoadingMore}
              data-testid="market-updates-load-more"
            >
              <LoadMoreLabel>
                {isLoadingMore ? "Loading…" : `Load ${loadCount} more`}
                <KeyboardArrowDownRounded fontSize="small" />
              </LoadMoreLabel>
            </Button>
          )}
        </Footer>
      )}

      {pendingDelete && (
        <DeletePopUp
          isOpen={Boolean(pendingDelete)}
          setIsOpen={closeDeleteModal}
          title="Delete Market Update"
          text={deleteText}
          isLoading={isDeleting}
          handleOnDelete={() => void handleConfirmDelete()}
        />
      )}
    </MarketUpdatesCard>
  );
};

export const MarketUpdates = React.memo(MarketUpdatesComponent);
